import { HirerNotificationPreferenceFormPresentation, NotificationFrequency, translationMessages as translationMessages$1, getNotificationOptionSettings, getNotificationOptionSetting } from "./styles/components/HirerNotificationPreferenceFormPresentation/index.mjs";
import { jsx } from "react/jsx-runtime";
import { createContext, useContext, useState } from "react";
import gql from "graphql-tag";
import { useTranslations } from "@vocab/react";
import { translations } from "./.vocab/index.mjs";
import { isEmail } from "@seek/validators-js";
const DEFAULT_NOTIFICATION_PREFERENCE = {
  frequency: "",
  previousFrequency: ""
};
const defaultValue = "";
const NotificationPreferenceContext = createContext({
  jobId: defaultValue,
  seekAdvertiserId: defaultValue,
  currentLoginUserEmail: defaultValue,
  currentLoginUserId: defaultValue,
  isLoadingPreference: false,
  setIsLoadingPreference: (_) => {
  },
  preference: DEFAULT_NOTIFICATION_PREFERENCE,
  setPreference: (_) => {
  },
  delayUpdate: false,
  isSetDefaultNotificationCompleted: false,
  setIsSetDefaultNotificationCompleted: (_) => {
  }
});
const useNotificationPreferenceContext = () => {
  const context = useContext(
    NotificationPreferenceContext
  );
  if (context === void 0) {
    throw new Error(
      "context must be used within a HirerNotificationPreferenceProvider"
    );
  }
  return context;
};
const HirerNotificationPreferenceForm = ({ id, size }) => {
  const { preference, setPreference, isLoadingPreference } = useNotificationPreferenceContext();
  const onEveryApplicationEmailChange = (email) => setPreference({
    ...preference,
    everyApplicationEmail: email,
    everyApplicationEmailError: ""
  });
  const onFrequencyChange = (frequency) => setPreference({ ...preference, frequency });
  const props = {
    ...preference,
    onEveryApplicationEmailChange,
    onFrequencyChange,
    id,
    size,
    loading: isLoadingPreference
  };
  return /* @__PURE__ */ jsx(HirerNotificationPreferenceFormPresentation, { ...props });
};
const HirerNotificationPreferenceProvider = ({
  jobId,
  seekAdvertiserId,
  currentLoginUserEmail,
  currentLoginUserId,
  apolloClient,
  delayUpdate = false,
  initialNotificationPreference,
  children
}) => {
  const [preference, setPreference] = useState({
    ...initialNotificationPreference ?? DEFAULT_NOTIFICATION_PREFERENCE,
    everyApplicationEmail: (initialNotificationPreference == null ? void 0 : initialNotificationPreference.everyApplicationEmail) ?? currentLoginUserEmail
  });
  const [isLoadingPreference, setIsLoadingPreference] = useState(false);
  const [
    isSetDefaultNotificationCompleted,
    setIsSetDefaultNotificationCompleted
  ] = useState(Boolean(initialNotificationPreference));
  const value = {
    jobId,
    seekAdvertiserId,
    currentLoginUserEmail,
    currentLoginUserId,
    isLoadingPreference,
    setIsLoadingPreference,
    preference,
    setPreference,
    apolloClient,
    delayUpdate,
    isSetDefaultNotificationCompleted,
    setIsSetDefaultNotificationCompleted
  };
  return /* @__PURE__ */ jsx(NotificationPreferenceContext.Provider, { value, children });
};
const SET_NOTIFICATION_PREFERENCE = gql`
  mutation SetNotificationPreferences(
    $input: SetNotificationPreferencesInput!
  ) {
    setNotificationPreferences(input: $input) {
      success
    }
  }
`;
const SET_DEFAULT_NOTIFICATION_PREFERENCE = gql`
  mutation SetDefaultNotificationPreference(
    $input: SetDefaultNotificationPreferenceInput!
  ) {
    setDefaultNotificationPreference(input: $input) {
      frequency
      applicationEmail
    }
  }
`;
const GET_NOTIFICATION_PREFERENCE = gql`
  query NotificationPreference($jobId: ID!, $advertiserId: ID!) {
    notificationPreference(jobId: $jobId, advertiserId: $advertiserId) {
      frequency
      applicationEmail
    }
  }
`;
const setDefaultNotificationPreferenceVariables = (jobId) => ({
  input: {
    jobId
  }
});
const setNotificationPreferenceVariables = (jobId, seekAdvertiserId, userId, frequency, everyApplicationEmail, delayUpdate) => ({
  input: {
    jobId,
    advertiserId: seekAdvertiserId,
    preferences: [
      {
        userId,
        frequency,
        applicationEmail: frequency === NotificationFrequency.EVERY_APPLICATION ? everyApplicationEmail : null
      }
    ],
    delayUpdate
  }
});
const setDefaultNotificationPreference = async (jobId, currentLoginUserEmail, client) => {
  var _a, _b;
  const notificationPreference = {
    frequency: "",
    previousFrequency: "",
    everyApplicationEmail: currentLoginUserEmail
  };
  try {
    const { data } = await client.mutate({
      mutation: SET_DEFAULT_NOTIFICATION_PREFERENCE,
      variables: setDefaultNotificationPreferenceVariables(jobId)
    });
    const frequency = ((_a = data == null ? void 0 : data.setDefaultNotificationPreference) == null ? void 0 : _a.frequency) ?? "";
    return {
      notificationPreference: {
        ...notificationPreference,
        frequency,
        previousFrequency: frequency,
        everyApplicationEmail: ((_b = data == null ? void 0 : data.setDefaultNotificationPreference) == null ? void 0 : _b.applicationEmail) || notificationPreference.everyApplicationEmail
      }
    };
  } catch {
    return { notificationPreference, error: true };
  }
};
const useSetDefaultNotificationPreference = () => {
  const {
    jobId,
    setPreference,
    setIsLoadingPreference,
    isLoadingPreference,
    apolloClient,
    currentLoginUserEmail,
    isSetDefaultNotificationCompleted,
    setIsSetDefaultNotificationCompleted
  } = useNotificationPreferenceContext();
  return async (onSetDefaultCompleted = (_) => {
  }) => {
    if (isSetDefaultNotificationCompleted || isLoadingPreference) {
      return;
    }
    setIsLoadingPreference(true);
    const result = await setDefaultNotificationPreference(
      jobId,
      currentLoginUserEmail,
      apolloClient
    );
    setPreference(result.notificationPreference);
    onSetDefaultCompleted(result);
    setIsSetDefaultNotificationCompleted(true);
    setIsLoadingPreference(false);
  };
};
const createSuccessResult = (notificationPreference) => ({
  success: true,
  notificationPreference
});
const createErrorResult = (errorMessage) => ({
  success: false,
  error: { errorMessage }
});
const createServerErrorResult = (errorMessage) => ({
  success: false,
  error: { errorMessage, isServerError: true }
});
const isValidateEmail = (emailValue) => {
  const value = emailValue ?? "";
  return value.length > 0 && isEmail.test({ value, validatorProps: {}, formValues: {} });
};
const notificationPreferenceValidationError = (frequency, everyApplicationEmail, messages) => {
  if (frequency === "") {
    return { errorMessage: messages["Please select a preference"] };
  }
  if (frequency === NotificationFrequency.EVERY_APPLICATION && !isValidateEmail(everyApplicationEmail)) {
    return { errorMessage: "", isEmailError: true };
  }
  return null;
};
const saveNotificationPreference = async (jobId, seekAdvertiserId, userId, frequency, everyApplicationEmail, delayUpdate, client) => {
  var _a;
  try {
    const { data } = await client.mutate({
      mutation: SET_NOTIFICATION_PREFERENCE,
      variables: setNotificationPreferenceVariables(
        jobId,
        seekAdvertiserId,
        userId,
        frequency,
        everyApplicationEmail,
        delayUpdate
      )
    });
    return ((_a = data == null ? void 0 : data.setNotificationPreferences) == null ? void 0 : _a.success) ?? false;
  } catch {
    return false;
  }
};
const saveNotificationPreferenceEventData = ({
  previousFrequency,
  frequency
}) => ({
  notificationScope: "self",
  notificationChangeStateFrom: previousFrequency,
  notificationChangeStateTo: frequency
});
const translationMessages = (t) => ({
  "Please select a preference": t("Please select a preference"),
  "Valid email address is required": t("Valid email address is required")
});
const useSaveNotificationPreference = () => {
  const { t } = useTranslations(translations);
  const messages = translationMessages(t);
  const {
    jobId,
    seekAdvertiserId,
    preference,
    setPreference,
    apolloClient,
    currentLoginUserId,
    delayUpdate
  } = useNotificationPreferenceContext();
  const { frequency, everyApplicationEmail } = preference;
  return async (onSaveTracking) => {
    const validationError = notificationPreferenceValidationError(
      frequency,
      everyApplicationEmail,
      messages
    );
    if (validationError !== null) {
      setPreference({
        ...preference,
        everyApplicationEmailError: validationError.isEmailError ? t("Valid email address is required") : void 0
      });
      return createErrorResult(validationError.errorMessage);
    }
    if (onSaveTracking) {
      onSaveTracking(saveNotificationPreferenceEventData(preference));
    }
    const success = await saveNotificationPreference(
      jobId,
      seekAdvertiserId,
      currentLoginUserId,
      frequency,
      everyApplicationEmail,
      delayUpdate,
      apolloClient
    );
    if (success) {
      const preferenceAfterSave = {
        ...preference,
        everyApplicationEmailError: void 0,
        previousFrequency: frequency
      };
      setPreference(preferenceAfterSave);
      return createSuccessResult(preferenceAfterSave);
    }
    setPreference({ ...preference, everyApplicationEmailError: void 0 });
    return createServerErrorResult(
      t("Sorry we are having trouble saving your preference now.")
    );
  };
};
const getNotificationPreference = async (jobId, seekAdvertiserId, currentLoginUserEmail, client) => {
  var _a, _b;
  try {
    const { data } = await client.query({
      query: GET_NOTIFICATION_PREFERENCE,
      variables: { jobId, advertiserId: seekAdvertiserId },
      fetchPolicy: "no-cache"
    });
    const frequency = (_a = data.notificationPreference) == null ? void 0 : _a.frequency;
    return {
      notificationPreference: {
        frequency,
        previousFrequency: frequency,
        everyApplicationEmail: ((_b = data.notificationPreference) == null ? void 0 : _b.applicationEmail) ?? currentLoginUserEmail
      }
    };
  } catch {
    return {
      notificationPreference: {
        frequency: "",
        previousFrequency: "",
        everyApplicationEmail: currentLoginUserEmail
      },
      error: true
    };
  }
};
const useGetNotificationPreference = () => {
  const {
    jobId,
    seekAdvertiserId,
    setPreference,
    apolloClient,
    currentLoginUserEmail,
    isLoadingPreference,
    setIsLoadingPreference
  } = useNotificationPreferenceContext();
  return async (onLoadCompleted = (_) => {
  }) => {
    if (isLoadingPreference) {
      return;
    }
    setIsLoadingPreference(true);
    const result = await getNotificationPreference(
      jobId,
      seekAdvertiserId,
      currentLoginUserEmail,
      apolloClient
    );
    setPreference(result.notificationPreference);
    onLoadCompleted(result);
    setIsLoadingPreference(false);
  };
};
const useNotificationPreference = ({
  preference
}) => {
  const { t } = useTranslations(translations);
  const messages = translationMessages$1(t);
  const notificationOptionSettings = getNotificationOptionSettings({
    messages
  });
  return getNotificationOptionSetting(
    notificationOptionSettings,
    (preference == null ? void 0 : preference.frequency) ?? ""
  );
};
export {
  GET_NOTIFICATION_PREFERENCE,
  HirerNotificationPreferenceForm,
  HirerNotificationPreferenceFormPresentation,
  HirerNotificationPreferenceProvider,
  NotificationFrequency,
  SET_DEFAULT_NOTIFICATION_PREFERENCE,
  useGetNotificationPreference,
  useNotificationPreference,
  useSaveNotificationPreference,
  useSetDefaultNotificationPreference
};
